import { useCallback, useMemo, useState } from 'react';

import { getApiClient } from '@apps/web/lib/api_client';
import { toast } from '@apps/web/lib/app_notifications';
import { readConversationOwnership } from '@apps/web/swr_data_fetching/conversation_ownership';
import { type ConversationPagesMutator } from '@apps/web/swr_data_fetching/conversation_pages';
import { type OrganizationConversationMetadata } from '@libraries/typescript/api_client';

import {
    applyConversationUnreadCountsToPages,
    persistInboxReadStates,
    revalidateOrganizationConversationPages,
} from './conversations-inbox-bulk-read';

/**
 * Bulk mark-read / mark-all / assign for the conversations inbox. Kept beside
 * the controller so the orchestration hook stays under the 1,000-line cap
 * without changing the public controller API.
 */
export function useConversationsInboxBulkActions(options: {
    readonly organizationId: string | null | undefined;
    readonly selection: ReadonlyArray<string>;
    readonly selectedConversations: ReadonlyArray<OrganizationConversationMetadata>;
    readonly unreadCount: number | undefined;
    readonly mutateConversations: ConversationPagesMutator;
    readonly mutateUnreadCount: () => Promise<unknown>;
    readonly assign: (
        conversationId: string,
        userId: string | null,
    ) => Promise<unknown>;
}) {
    const {
        organizationId,
        selection,
        selectedConversations,
        unreadCount,
        mutateConversations,
        mutateUnreadCount,
        assign,
    } = options;

    const [isBulkReadPending, setIsBulkReadPending] = useState(false);
    const [isMarkingAllAsRead, setIsMarkingAllAsRead] = useState(false);

    const bulkAssignee = useMemo(() => {
        const first = selectedConversations[0];
        if (first === undefined) {
            return { userId: null, userName: null };
        }
        const firstOwnership = readConversationOwnership(first);
        const allSame = selectedConversations.every(
            (conversation) =>
                readConversationOwnership(conversation).assigned_to_user_id ===
                firstOwnership.assigned_to_user_id,
        );
        if (!allSame) {
            return { userId: null, userName: null };
        }
        return {
            userId: firstOwnership.assigned_to_user_id,
            userName: firstOwnership.assigned_to_user_name,
        };
    }, [selectedConversations]);

    const onBulkMarkReadState = useCallback(
        async (nextUnreadCount: 0 | 1) => {
            if (
                !organizationId ||
                selection.length === 0 ||
                isBulkReadPending
            ) {
                return;
            }
            const conversationIds = selection;
            if (conversationIds.length === 0) {
                return;
            }
            setIsBulkReadPending(true);
            try {
                const { succeededIds, failedIds } =
                    await persistInboxReadStates(
                        organizationId,
                        conversationIds,
                        nextUnreadCount,
                    );
                if (succeededIds.length > 0) {
                    await mutateConversations(
                        (pages) =>
                            applyConversationUnreadCountsToPages(
                                pages,
                                new Set(succeededIds),
                                nextUnreadCount,
                            ),
                        { revalidate: true },
                    );
                    void revalidateOrganizationConversationPages(
                        organizationId,
                    );
                    void mutateUnreadCount();
                }
                if (failedIds.length > 0) {
                    toast.error(
                        succeededIds.length > 0
                            ? 'Updated some conversations; others failed'
                            : 'Could not update read state',
                        {
                            persistNotification: false,
                        },
                    );
                    return;
                }
                toast.success(
                    nextUnreadCount === 0
                        ? 'Marked as read'
                        : 'Marked as unread',
                    {
                        persistNotification: false,
                    },
                );
            } catch {
                toast.error('Could not update read state', {
                    persistNotification: false,
                });
            } finally {
                setIsBulkReadPending(false);
            }
        },
        [
            isBulkReadPending,
            mutateConversations,
            mutateUnreadCount,
            organizationId,
            selection,
        ],
    );

    const handleMarkAllAsRead = useCallback(async () => {
        if (!organizationId || isMarkingAllAsRead || (unreadCount ?? 0) === 0) {
            return;
        }
        setIsMarkingAllAsRead(true);
        try {
            const response = await getApiClient().post({
                url: '/organizations/{organization_id}/conversations/mark-all-read',
                pathParams: {
                    organization_id: organizationId,
                },
            });
            const updatedIds = new Set(response.updated_conversation_ids ?? []);
            await mutateConversations(
                (pages) =>
                    applyConversationUnreadCountsToPages(pages, updatedIds, 0),
                { revalidate: true },
            );
            void revalidateOrganizationConversationPages(organizationId);
            void mutateUnreadCount();
            toast.success('Marked all as read', {
                persistNotification: false,
            });
        } catch {
            toast.error('Could not mark all as read', {
                persistNotification: false,
            });
        } finally {
            setIsMarkingAllAsRead(false);
        }
    }, [
        isMarkingAllAsRead,
        mutateConversations,
        mutateUnreadCount,
        organizationId,
        unreadCount,
    ]);

    const onBulkAssign = useCallback(
        async (userId: string | null) => {
            const liveConversations = selectedConversations;
            if (liveConversations.length === 0 || isBulkReadPending) {
                return;
            }
            setIsBulkReadPending(true);
            try {
                // Per-conversation assignment commits independently, so settle
                // each call and report partial success instead of treating a
                // mixed outcome as a single undifferentiated failure.
                const results = await Promise.allSettled(
                    liveConversations.map((conversation) =>
                        assign(conversation.id, userId),
                    ),
                );
                const succeededIds: Array<string> = [];
                const failedIds: Array<string> = [];
                for (const [index, result] of results.entries()) {
                    const conversation = liveConversations[index];
                    if (conversation === undefined) {
                        continue;
                    }
                    if (result.status === 'fulfilled') {
                        succeededIds.push(conversation.id);
                    } else {
                        failedIds.push(conversation.id);
                    }
                }
                if (succeededIds.length > 0 && organizationId) {
                    void revalidateOrganizationConversationPages(
                        organizationId,
                    );
                }
                if (failedIds.length > 0) {
                    toast.error(
                        succeededIds.length > 0
                            ? userId
                                ? 'Assigned some conversations; others failed'
                                : 'Unassigned some conversations; others failed'
                            : 'Could not assign conversations',
                        {
                            persistNotification: false,
                        },
                    );
                    return;
                }
                toast.success(userId ? 'Assigned' : 'Unassigned', {
                    persistNotification: false,
                });
            } catch {
                toast.error('Could not assign conversations', {
                    persistNotification: false,
                });
            } finally {
                setIsBulkReadPending(false);
            }
        },
        [assign, isBulkReadPending, organizationId, selectedConversations],
    );

    return {
        bulkAssignee,
        handleMarkAllAsRead,
        isBulkReadPending,
        isMarkingAllAsRead,
        onBulkAssign,
        onBulkMarkReadState,
    };
}
